import { StringUtils } from "Global/Base/Utils/StringUtils";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { FIT_TO_CONTENT_MARGIN } from "../../constants";
import { StretchType } from "../../Controls/ControlEnums";
import { UIManager as uiManager } from "../../UIManager";
import { AlignPanelValidation } from "./AlignPanelValidation";
import { BaseAlignElement } from "./BaseAlignElement";

const EXTENDED_PROTPERTIE_ATTRIBUTE = "data-extendedproperties";

/**
 * Pomocny element pro pozicovani a vlozeni jednotlivych controlu s captiony
 */
export class ControlElement extends BaseAlignElement {
    public control: HTMLElement;
    public label: BaseAlignElement;

    private readonly _stylesheet: HTMLStyleElement;
    private _captionControl: BaseAlignElement;
    private _useLabel: boolean = true;
    private _isGroupBox: boolean = false;
    private _isInGroupBox: boolean = false;
    private _alwaysShowCaption: boolean = true;

    public get hasLabel(): boolean {
        return this._useLabel;
    }

    public set hasLabel(v: boolean) {
        this._useLabel = v;
    }

    constructor(control: HTMLElement, stylesheet: HTMLStyleElement) {
        super(createElement({ classes: "ControlContainer", id: uiManager.instance.generateElementID() }));

        this._stylesheet = stylesheet;

        const controlName = control.getAttribute("data-tsccontrol");
        this._isGroupBox = controlName === "GroupBox";
        this._isInGroupBox = control.parentElement && control.parentElement.getAttribute("data-tsccontrol") === "GroupBox";
        this._alwaysShowCaption = DataConverter.toBoolean(control.getAttribute("data-alwaysshowcaption"), true);

        this.element.classList.add(this._isGroupBox ? "GroupBoxContainer" : "AlignPanelSubcomponent");

        if (controlName) {
            this.element.classList.add(`${controlName.toLowerCase()}-container`);
        }

        if (control.hasAttribute(EXTENDED_PROTPERTIE_ATTRIBUTE)) {
            this.element.setAttribute(EXTENDED_PROTPERTIE_ATTRIBUTE, control.getAttribute(EXTENDED_PROTPERTIE_ATTRIBUTE));
        }

        const parentData = control.getAttribute("data-parent-data");
        if (parentData)
            this.element.setAttribute(parentData, "");

        this.control = control;
        this._parseAlignData(control);

        this._useLabel = !DataConverter.toBoolean(this.control.getAttribute("data-ignorecaption"), false);

        if (this._useLabel)
            this._createCaption();
        this._createContainer();
    }

    public static getCaptionElement(forId: string, locKey: string): BaseAlignElement {
        const label = new BaseAlignElement(createElement({ type: "label", classes: "Caption", id: forId + "-label", attrs: { for: forId } }));
        if (locKey)
            Localization.addLocTxtData(label.element, locKey);

        return label;
    }

    private _createCaption(): void {
        if (this.label) {
            this.label.element.remove();
        }

        // Takto vygenerované ID je přiřazeno dané komponentě, jakmile je vytvářena
        const componentId = uiManager.instance.generateElementID();
        this.control.setAttribute("data-componentId", componentId);
        this.label = ControlElement.getCaptionElement(componentId, this.control.getAttribute("data-captionLocKey"));

        if (this._alwaysShowCaption) {
            this.label.element.classList.add("AlwaysVisible");
        }
    }

    private _createContainer(): void {
        //document.insertBefore(this.Element, this.Control);
        this._captionControl = new BaseAlignElement(createElement({ id: uiManager.instance.generateElementID() }));

        if (this._useLabel) {
            this._captionControl.element.classList.add("CaptionContainer");
            this._captionControl.element.appendChild(this.label.element);
            this.element.classList.add("has-caption");
            this.element.appendChild(this._captionControl.element);
        }

        this.element.appendChild(this.control);
    }

    private _adjustSizeToContent(): void {
        if (this._isGroupBox) {
            const groupBox = this.element.querySelector("[data-tsccontrol=\"GroupBox\"]") as HTMLElement;
            if (groupBox) {
                const stretchType = BaseAlignElement.parseStretchType(groupBox.getAttribute("data-stretch"));
                if (stretchType === StretchType.none)
                    return;

                const children = Array.from(groupBox.children) as HTMLElement[];
                const posElements: BaseAlignElement[] = [];

                for (const child of children) {
                    const ch = new BaseAlignElement(child);
                    if (!AlignPanelValidation.isDockingElement(ch)) {
                        let right: string;
                        if ((ch.element.hasAttribute("data-tscitem") && (right = ch.element.getAttribute("data-tscitem")) && right && right.toLowerCase().startsWith("rights."))
                            || ch.element.hasAttribute("data-ignore-positioning"))
                            continue;
                        posElements.push(new BaseAlignElement(ch.element));
                    }
                }

                const adjustedSize = ControlElement.getContentSize(groupBox, stretchType, posElements);

                if (adjustedSize.width) {
                    this._width = adjustedSize.width;
                }

                if (adjustedSize.height) {
                    this._height = adjustedSize.height;
                }
            }
        }
    }

    public createStyleData(): void {
        this._adjustSizeToContent();

        let styleData = `.AlignPanel.fits #${this.element.id} {`;
        styleData += this.getSizeCssString({ top: this._isInGroupBox ? 7 : undefined });
        styleData += "}";

        if (this._useLabel) {
            if (!this._isGroupBox) {
                styleData += `.AlignPanel.fits #${this._captionControl.element.id} {`;
                styleData += `  top: ${BaseAlignElement.parseNum(this.control.getAttribute("data-captiontop")) - this.top}px;\n`;
                styleData += `  left: ${BaseAlignElement.parseNum(this.control.getAttribute("data-captionleft")) - this.left}px;\n`;
                styleData += `  max-width: ${this.width}px;\n`;
                styleData += "}";
            }
        }

        this._stylesheet.innerHTML += styleData;

        //nastaveni minimalni vysky v pripade ze neni fits u listframe
        if (this.control.getAttribute("data-tsccontrol") == "ListFrame") {
            let styleListFrame = `.AlignPanelSubcomponent#${StringUtils.stringToHTMLText(this.element.id)} .ListFrame {`;
            styleListFrame += `min-height: ${this.height}px; `;
            styleListFrame += "}";
            this._stylesheet.innerHTML += styleListFrame;
        }
    }

    public override getSizeCssString(offset?: { width?: number; height?: number; top?: number; left?: number }): string {
        let styleData = super.getSizeCssString(offset);

        if (this.height != undefined && this._isGroupBox) {
            styleData += `min-height: ${offset && offset.height ? this.height + offset.height : this.height}px; `;
        }

        return styleData;
    }

    /**
     * Spočítá minimální výšku a šířku potřebnou pro vykreslení html elementu a jeho obsahu.
     * @param _element HTMLElement, pro který se má spočítat minimální výška a šířka
     * @param stretchType způsob jakým se má výška přizpůsobit obsahu.
     * @return Rozměry groupboxu
     */
    public static getContentSize(_element: HTMLElement, stretchType: StretchType, posElements: BaseAlignElement[]): Size {
        const result = new Size();

        if (stretchType === StretchType.none)
            return result;

        if (stretchType === StretchType.both || stretchType === StretchType.horizontal)
            result.width = Math.max(...posElements.map(i => i.left + i.width)) + FIT_TO_CONTENT_MARGIN;
        if (stretchType === StretchType.both || stretchType === StretchType.vertical)
            result.height = Math.max(...posElements.map(i => i.top + i.height)) + FIT_TO_CONTENT_MARGIN;
        return result;
    }
}

export class Size {
    public width?: number;
    public height?: number;
}
